import React, { useEffect, useRef } from "react";

export default function MessageContextMenu({
  isOpen,
  message,
  anchorPosition, // { x, y } or null
  onClose,
  onReply,
  onReact,
  onCopy,
  onStar,
  onPin,
}) {
  const menuRef = useRef(null);

  // Close on Escape or click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !message) return null;

  const isDeleted = message.is_deleted || message.deleted_for_everyone;

  // Calculate safe viewport positioning
  const style = {};
  if (anchorPosition && anchorPosition.x !== undefined && anchorPosition.y !== undefined) {
    const menuWidth = 190;
    const menuHeight = 280;
    const padding = 10;

    let x = anchorPosition.x;
    let y = anchorPosition.y;

    if (x + menuWidth > window.innerWidth - padding) {
      x = window.innerWidth - menuWidth - padding;
    }
    if (x < padding) x = padding;

    if (y + menuHeight > window.innerHeight - padding) {
      y = window.innerHeight - menuHeight - padding;
    }
    if (y < padding) y = padding;

    style.position = "fixed";
    style.top = `${y}px`;
    style.left = `${x}px`;
    style.zIndex = 9999;
  }

  return (
    <div
      ref={menuRef}
      className="wa-context-menu"
      style={style}
      role="menu"
      aria-orientation="vertical"
      onClick={(e) => e.stopPropagation()}
    >
      {/* 1. Reply */}
      {!isDeleted && (
        <button
          type="button"
          className="wa-context-item"
          role="menuitem"
          onClick={() => {
            onClose();
            onReply(message);
          }}
        >
          <i className="bi bi-reply wa-item-icon"></i>
          <span>Reply</span>
        </button>
      )}

      {/* 3. React */}
      {!isDeleted && (
        <button
          type="button"
          className="wa-context-item"
          role="menuitem"
          onClick={() => {
            onClose();
            onReact(message);
          }}
        >
          <i className="bi bi-emoji-smile wa-item-icon"></i>
          <span>React</span>
        </button>
      )}

      {/* 4. Copy text */}
      {!isDeleted && message.message_body && (
        <button
          type="button"
          className="wa-context-item"
          role="menuitem"
          onClick={() => {
            onClose();
            onCopy(message.message_body);
          }}
        >
          <i className="bi bi-clipboard wa-item-icon"></i>
          <span>Copy</span>
        </button>
      )}

      {/* 6. Star / Unstar */}
      <button
        type="button"
        className="wa-context-item"
        role="menuitem"
        onClick={() => {
          onClose();
          onStar(message);
        }}
      >
        <i
          className={`bi ${message.is_starred ? "bi-star-fill text-warning" : "bi-star"} wa-item-icon`}
        ></i>
        <span>{message.is_starred ? "Unstar" : "Star"}</span>
      </button>

      {/* 7. Pin / Unpin */}
      <button
        type="button"
        className="wa-context-item"
        role="menuitem"
        onClick={() => {
          onClose();
          onPin(message);
        }}
      >
        <i
          className={`bi ${message.is_pinned ? "bi-pin-fill text-success" : "bi-pin-angle"} wa-item-icon`}
        ></i>
        <span>{message.is_pinned ? "Unpin" : "Pin"}</span>
      </button>
    </div>
  );
}
